const generalActions = [
  {
    id: 'screen',
    label: 'Ayuda de esta pantalla',
    response: {
      intro: 'Usá esta ayuda como una guía rápida para trabajar en la pantalla actual sin perder el hilo de la operación.',
      steps: [
        'Revisá primero el título y los filtros disponibles para confirmar qué información estás consultando.',
        'Usá los listados para abrir un registro y verificar su detalle antes de aplicar una acción.',
        'Confirmá los datos obligatorios y el estado operativo antes de guardar, emitir o cerrar una operación.',
      ],
      tip: 'Si necesitás localizar un documento puntual, usá la consulta de Sina con el número de guía, foja o reparto.',
    },
  },
  {
    id: 'guides',
    label: 'Guías y trazabilidad',
    response: {
      intro: 'Cada guía identifica un envío. Su trazabilidad muestra los cambios y eventos que tuvo desde la emisión hasta su entrega, retención o cierre.',
      steps: [
        'Buscá la guía por número, remitente, destinatario o estado y abrí su detalle.',
        'En el detalle revisá los datos del envío, la sucursal de origen y destino, el estado actual y los importes valorizados.',
        'Consultá la línea de tiempo para ver cuándo se emitió, se incorporó a una foja, entró en tránsito, fue recibida o asignada a reparto.',
        'Si hay una novedad, verificá su motivo y responsable antes de continuar con el despacho, la entrega o la facturación.',
      ],
      tip: 'Una guía entregada o anulada conserva su historial: no se pierde la trazabilidad al consultar operaciones anteriores.',
    },
  },
  {
    id: 'fojas',
    label: 'Fojas y despachos',
    response: {
      intro: 'Las fojas agrupan las guías que viajan juntas entre sucursales y permiten controlar cada despacho de salida o recepción.',
      steps: [
        'Creá o abrí una foja para la sucursal y recorrido correspondientes.',
        'Incorporá las guías verificando que estén listas para despachar.',
        'Controlá el listado antes de cerrar la foja y registrá la salida.',
        'En destino, recibí la foja y revisá cualquier diferencia antes de derivar las guías a reparto.',
      ],
      tip: 'No cierres una foja si faltan guías o hay diferencias sin justificar: el control previo evita errores de tránsito.',
    },
  },
  {
    id: 'deliveries',
    label: 'Repartos y entregas',
    response: {
      intro: 'Los repartos organizan la última etapa de distribución: asignación de guías, recorrido, entrega y registro de novedades.',
      steps: [
        'Armá el reparto con las guías recibidas en la sucursal de destino.',
        'Asigná el repartidor o vehículo y verificá el recorrido planificado.',
        'Durante la distribución, registrá la entrega, intento, retención o cualquier novedad real.',
        'Al finalizar, revisá las pendientes y cerrá el reparto sólo cuando las novedades estén informadas.',
      ],
      tip: 'Una novedad bien registrada explica por qué una guía no fue entregada y permite definir el siguiente paso operativo.',
    },
  },
  {
    id: 'collections',
    label: 'Cobranzas y minutas',
    response: {
      intro: 'Las minutas concentran los cobros informados para una sucursal y ayudan a controlar importes, medios de pago y rendición.',
      steps: [
        'Abrí la minuta correspondiente a la sucursal y fecha de trabajo.',
        'Incorporá los cobros identificando el documento, importe y medio de pago correctos.',
        'Compará el total informado con el efectivo, transferencias, cheques u otros valores recibidos.',
        'Revisá las diferencias y dejá el respaldo antes de confirmar o cerrar la minuta.',
      ],
      tip: 'No confirmes una cobranza sin comprobar el medio de pago y su comprobante: después impacta en cuenta corriente y conciliación.',
    },
  },
  {
    id: 'billing',
    label: 'Facturación',
    response: {
      intro: 'La facturación se construye a partir de guías previamente valorizadas para que cada importe facturado conserve su origen operativo.',
      steps: [
        'Verificá que las guías estén valorizadas y que sus datos de cliente, pagador y condición impositiva sean correctos.',
        'Seleccioná las guías que corresponde incluir en la factura y revisá los conceptos e importes.',
        'Controlá el comprobante antes de emitirlo, especialmente el cliente, la sucursal y el total.',
        'Luego consultá el detalle de la factura para verificar las guías vinculadas y su trazabilidad.',
      ],
      tip: 'Si una guía requiere una corrección de tarifa, resolvela antes de facturar para evitar reliquidaciones posteriores.',
    },
  },
  {
    id: 'shortcuts',
    label: 'Atajos de teclado',
    response: {
      intro: 'Estos accesos agilizan las consultas frecuentes sin interrumpir la operación.',
      steps: [
        'Presioná F1 para abrir la ayuda contextual de Sina desde cualquier pantalla.',
        'Usá Esc para cerrar paneles abiertos cuando el foco no esté dentro de un campo de edición.',
        'En la consulta de Sina podés escribir el número de una guía, foja o reparto para abrir su detalle.',
      ],
      tip: 'Si F1 está reservado por el navegador o el equipo, abrí Sina desde el botón flotante de la esquina inferior.',
    },
  },
];

export const sinaGeneralHelp = {
  title: 'Ayuda operativa',
  description: 'Elegí un tema para consultar una guía breve de Sinamon.',
  actions: generalActions,
};

const bySection = [
  {
    matches: ['/guias'],
    title: 'Ayuda sobre guías',
    description: 'Desde aquí podés emitir, consultar y seguir el estado operativo de cada envío.',
  },
  {
    matches: ['/fojas'],
    title: 'Ayuda sobre fojas',
    description: 'Las fojas agrupan las guías que se trasladan entre sucursales.',
  },
  {
    matches: ['/repartos'],
    title: 'Ayuda sobre repartos',
    description: 'El reparto organiza las entregas locales y sus novedades.',
  },
  {
    matches: ['/cobranzas', '/tesoreria/cobranzas'],
    title: 'Ayuda sobre cobranzas',
    description: 'Las minutas permiten registrar y controlar los cobros de cada sucursal.',
  },
  {
    matches: ['/facturacion'],
    title: 'Ayuda sobre facturación',
    description: 'La facturación debe generarse desde guías previamente valorizadas.',
  },
];

export function getSinaHelp(pathname = '/') {
  const section = bySection.find(({ matches }) => matches.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  ));

  return section ? { ...sinaGeneralHelp, ...section } : sinaGeneralHelp;
}
